import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { auth } from '@/lib/firebase'
import { signInAnonymously, getIdToken } from 'firebase/auth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react'

interface ConfigStatus {
  name: string
  value: string | undefined
  isSet: boolean
  isValid?: boolean
  note?: string
}

function maskValue(value: string | undefined, showChars = 8): string {
  if (!value) return 'NOT SET'
  if (value.length <= showChars) return value
  return `${value.substring(0, showChars)}...${value.substring(value.length - 4)}`
}

export default function DebugPage() {
  const [configs, setConfigs] = useState<ConfigStatus[]>([])
  const [firebaseTestResult, setFirebaseTestResult] = useState<{
    success: boolean
    message: string
    details?: any
  } | null>(null)
  const [isTestingFirebase, setIsTestingFirebase] = useState(false)
  const [backendTestResult, setBackendTestResult] = useState<{
    success: boolean
    message: string
    details?: any
  } | null>(null)
  const [isTestingBackend, setIsTestingBackend] = useState(false)

  useEffect(() => {
    // Check all environment variables
    const envVars = [
      { name: 'VITE_FIREBASE_API_KEY', sensitive: true },
      { name: 'VITE_FIREBASE_AUTH_DOMAIN', sensitive: false },
      { name: 'VITE_FIREBASE_PROJECT_ID', sensitive: false },
      { name: 'VITE_FIREBASE_STORAGE_BUCKET', sensitive: false },
      { name: 'VITE_FIREBASE_MESSAGING_SENDER_ID', sensitive: true },
      { name: 'VITE_FIREBASE_APP_ID', sensitive: true },
      { name: 'VITE_FIREBASE_MEASUREMENT_ID', sensitive: false, optional: true },
      { name: 'VITE_BACKEND_URL', sensitive: false },
      { name: 'VITE_WS_URL', sensitive: false },
      { name: 'VITE_ENV', sensitive: false },
      { name: 'VITE_ADMIN_ASSETS_BUCKET', sensitive: false, optional: true },
      { name: 'VITE_ADMIN_ASSETS_CDN_URL', sensitive: false, optional: true },
    ]

    const configStatuses: ConfigStatus[] = envVars.map(envVar => {
      const value = import.meta.env[envVar.name]
      const isSet = value !== undefined && value !== '' && value !== 'undefined'
      
      let isValid = isSet
      let note = ''
      
      // Validate specific patterns
      if (isSet) {
        if (envVar.name === 'VITE_FIREBASE_API_KEY' && value.includes('your-')) {
          isValid = false
          note = 'Placeholder value detected'
        }
        if (envVar.name === 'VITE_FIREBASE_PROJECT_ID') {
          if (value === 'ponder-eaeff') note = 'Staging project'
          if (value === 'pond-35706') note = 'Production project'
        }
        if (envVar.name === 'VITE_ENV') {
          note = `Running in ${value} mode`
        }
      } else if (envVar.optional) {
        note = 'Optional'
      }
      
      return {
        name: envVar.name,
        value: envVar.sensitive ? maskValue(value) : value,
        isSet,
        isValid,
        note
      }
    })
    
    setConfigs(configStatuses)
  }, [])

  const testFirebaseConnection = async () => {
    setIsTestingFirebase(true)
    setFirebaseTestResult(null)
    
    try {
      // Try anonymous sign in to test Firebase connection
      const userCredential = await signInAnonymously(auth)
      const user = userCredential.user
      
      // Get ID token to verify auth is working
      const token = await getIdToken(user)
      
      setFirebaseTestResult({
        success: true,
        message: 'Firebase connection successful!',
        details: {
          uid: user.uid,
          tokenLength: token.length,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID
        }
      })
      
      // Clean up - sign out the anonymous user
      await auth.signOut()
    } catch (error: any) {
      setFirebaseTestResult({
        success: false,
        message: 'Firebase connection failed',
        details: {
          error: error.message,
          code: error.code,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID
        }
      })
    } finally {
      setIsTestingFirebase(false)
    }
  }

  const testBackendConnection = async () => {
    setIsTestingBackend(true)
    setBackendTestResult(null)
    
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL
      if (!backendUrl) {
        throw new Error('Backend URL not configured')
      }
      
      const response = await fetch(`${backendUrl}/health`)
      const data = await response.json()
      
      setBackendTestResult({
        success: response.ok,
        message: response.ok ? 'Backend connection successful!' : 'Backend returned non-OK status',
        details: {
          status: response.status,
          url: backendUrl,
          response: data
        }
      })
    } catch (error: any) {
      setBackendTestResult({
        success: false,
        message: 'Backend connection failed',
        details: {
          error: error.message,
          url: import.meta.env.VITE_BACKEND_URL
        }
      })
    } finally {
      setIsTestingBackend(false)
    }
  }

  const criticalConfigs = configs.filter(c => !c.isValid && !c.note?.includes('Optional'))
  const allCriticalValid = criticalConfigs.length === 0

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-8">Admin Panel Debug</h1>
      
      <Alert className={`mb-6 ${allCriticalValid ? 'border-green-500' : 'border-red-500'}`}>
        <AlertDescription className="flex items-center gap-2">
          {allCriticalValid ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>All critical environment variables are configured</span>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-red-500" />
              <span>{criticalConfigs.length} critical environment variable(s) are missing or invalid</span>
            </>
          )}
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Environment Configuration</CardTitle>
            <CardDescription>Current environment variable values</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {configs.map((config) => (
                <div key={config.name} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1">
                    <div className="font-mono text-sm">{config.name.replace('VITE_', '')}</div>
                    <div className="text-sm text-muted-foreground">
                      {config.value || 'NOT SET'}
                      {config.note && <span className="ml-2 text-xs">({config.note})</span>}
                    </div>
                  </div>
                  <div className="ml-2">
                    {config.isValid ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : config.note?.includes('Optional') ? (
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Firebase Connection Test</CardTitle>
              <CardDescription>Test connection to Firebase Auth</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={testFirebaseConnection} 
                disabled={isTestingFirebase}
                className="w-full mb-4"
              >
                {isTestingFirebase ? 'Testing...' : 'Test Firebase Connection'}
              </Button>
              
              {firebaseTestResult && (
                <Alert className={firebaseTestResult.success ? 'border-green-500' : 'border-red-500'}>
                  <AlertDescription>
                    <div className="font-semibold mb-2">{firebaseTestResult.message}</div>
                    <pre className="text-xs overflow-auto bg-muted p-2 rounded mt-2">
                      {JSON.stringify(firebaseTestResult.details, null, 2)}
                    </pre>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Backend Connection Test</CardTitle>
              <CardDescription>Test connection to backend API</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={testBackendConnection} 
                disabled={isTestingBackend}
                className="w-full mb-4"
              >
                {isTestingBackend ? 'Testing...' : 'Test Backend Connection'}
              </Button>
              
              {backendTestResult && (
                <Alert className={backendTestResult.success ? 'border-green-500' : 'border-red-500'}>
                  <AlertDescription>
                    <div className="font-semibold mb-2">{backendTestResult.message}</div>
                    <pre className="text-xs overflow-auto bg-muted p-2 rounded mt-2">
                      {JSON.stringify(backendTestResult.details, null, 2)}
                    </pre>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}